import { Fragment, h } from '@atomify/jsx';
import {
    ExtendedProjectPhase,
    ExtendedPropertyType,
    GeneratedConstructionNumberStatus,
    PropertyTypeStatus,
} from '@bpd-library/utilities';
import { formatLivingArea, formatPrice } from '@utilities/api-formatters';
import { translateItem } from '@utilities/hooks';
import { TranslateOptions } from '@utilities/hooks/use-translate.types';
import { translateEnumeration } from '@utilities/translate-enumeration';

type ConstructionNumberCountPerStatus = { [K in GeneratedConstructionNumberStatus]?: number };
type ConstructionNumberCountTranslationsPerStatus = {
    [K in GeneratedConstructionNumberStatus]?: string;
};

const constructionNumberCountPerStatusTranslation: ConstructionNumberCountTranslationsPerStatus = {
    InPreparation:
        'AreaSubscription.Modal.Step1.AvailableListCardDetail.ConstructionNumbersInPreparation',
    Available: 'AreaSubscription.Modal.Step1.AvailableListCardDetail.ConstructionNumbersAvailable',
    AssigningOn:
        'AreaSubscription.Modal.Step1.AvailableListCardDetail.ConstructionNumbersAssigningOn',
    InOption: 'AreaSubscription.Modal.Step1.AvailableListCardDetail.ConstructionNumbersInOption',
    Sold: 'AreaSubscription.Modal.Step1.AvailableListCardDetail.ConstructionNumbersSold',
};

const propertyTypeCountInPreparationTranslationKey =
    'AreaSubscription.Modal.Step1.AvailableListCardDetail.PropertiesInPreparation';

const propertyTypeExcludedStatuses: PropertyTypeStatus[] = ['Rented', 'Sold', 'UnderConstruction'];

const constructionNumberIncludedStatuses: GeneratedConstructionNumberStatus[] = [
    'Available',
    'InOption',
    'AssigningOn',
    'InPreparation',
    'Sold',
];
const projectInPreparationConstructionNumberIncludedStatuses: GeneratedConstructionNumberStatus[] =
    ['InPreparation', 'Available', 'InOption'];
const projectOnSaleConstructionNumberIncludedStatuses: GeneratedConstructionNumberStatus[] = [
    'Available',
    'InOption',
    'AssigningOn',
    'Sold',
];

const createCountedTranslation = async (
    count: number,
    translationKey: string,
    counterId: string,
) => {
    const translationOptions: TranslateOptions = {
        replace: [{ id: counterId, value: `<b>${count}</b>` }],
        plurality: [{ id: counterId, value: count }],
    };

    return await translateItem(translationKey, translationOptions);
};

export const renderProjectPhaseDetail = async (
    projectPhase: ExtendedProjectPhase,
): Promise<Element | undefined> => {
    const { constructionNumbers: cNrs, propertyTypes: allPropertyTypes } = projectPhase;
    const propertyTypes = allPropertyTypes?.filter(({ status }) => {
        return !!status && !propertyTypeExcludedStatuses.includes(status);
    });
    const translations: string[] = [];
    const element = (<p />) as Element;

    const constructionNumberCountPerStatus = cNrs?.reduce<ConstructionNumberCountPerStatus>(
        (countPerStatus, constructionNumber) => {
            const { status } = constructionNumber;
            if (!status || !constructionNumberIncludedStatuses.includes(status)) {
                return countPerStatus;
            }

            const currentValueForStatus = countPerStatus[status];
            if (!currentValueForStatus) countPerStatus[status] = 1;
            else countPerStatus[status] = currentValueForStatus + 1;
            return countPerStatus;
        },
        {},
    );

    const projectInPreparationHasNoConstructionNumbers =
        projectPhase.status === 'InPreparation' &&
        !Object.keys(constructionNumberCountPerStatus ?? {}).length;

    if (!constructionNumberCountPerStatus || projectInPreparationHasNoConstructionNumbers) {
        const count = propertyTypes?.length;
        const translationKey = propertyTypeCountInPreparationTranslationKey;
        if (!count || !translationKey) return;

        element.innerHTML = await createCountedTranslation(
            count,
            translationKey,
            'countPropertiesInPreparation',
        );
        return element;
    }

    let statuses: GeneratedConstructionNumberStatus[] = [];
    if (projectPhase.status === 'InPreparation') {
        statuses = projectInPreparationConstructionNumberIncludedStatuses;
    } else if (projectPhase.status === 'OnSale') {
        statuses = projectOnSaleConstructionNumberIncludedStatuses;
    }

    for (const status of statuses) {
        const count = constructionNumberCountPerStatus[status];
        const translationKey = constructionNumberCountPerStatusTranslation[status];
        if (!count || !translationKey) continue;

        translations.push(
            await createCountedTranslation(
                count,
                translationKey,
                `countConstructionNumbers${status}`,
            ),
        );
    }
    element.innerHTML = await translateEnumeration(translations);
    return element;
};

export const renderPropertyTypeDetail = (propertyType: ExtendedPropertyType): Element => {
    const price = formatPrice({ propertyType });
    const area = formatLivingArea(propertyType);
    return (
        <Fragment>
            {price && (
                <div>
                    {price.prefix && (
                        <span class="u-status-large u-color--grey-30 u-margin-right-1">
                            {price.prefix}
                        </span>
                    )}
                    {price.amount && price.amount}
                    {price.suffix && (
                        <span class="u-status-large u-color--grey-30 u-margin-left-1">
                            {price.suffix}
                        </span>
                    )}
                </div>
            )}
            {area && (
                <div>
                    <span class="u-status-large u-color--grey-30 u-margin-right">{area.value}</span>
                    {area.suffix && (
                        <span class="u-status-large u-color--grey-30 u-margin-left-1">
                            {area.suffix}
                        </span>
                    )}
                </div>
            )}
        </Fragment>
    );
};
